import { CheckCircle2, Database, GitBranch, Layers } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatabaseStatus, MigrationStatus } from '@/lib/types';

interface Props {
    databaseInfo: DatabaseStatus | null;
    migration: MigrationStatus | null;
}

function shortVersion(version: string): string {
    const match = version.match(/PostgreSQL\s+([\d.]+)/i);
    return match ? `PostgreSQL ${match[1]}` : version;
}

export function DatabaseCheck({ databaseInfo, migration }: Props) {
    const isConnected = !!databaseInfo;

    return (
        <Card className="gap-0 py-0 overflow-hidden">
            <CardHeader className="bg-muted/40 border-b px-4 py-3 flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-sm font-semibold">
                        {databaseInfo?.databaseName ?? 'Database'}
                    </CardTitle>
                    {databaseInfo && (
                        <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground"
                            title={databaseInfo.version}
                        >
                            {shortVersion(databaseInfo.version)}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span
                        className={`h-2 w-2 rounded-full ${isConnected ? 'bg-primary animate-pulse' : 'bg-destructive'}`}
                    />
                    <span className="text-xs text-muted-foreground">
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                </div>
            </CardHeader>

            {isConnected && (
                <CardContent className="p-0">
                    <div className="grid grid-cols-3 divide-x border-b">
                        <div className="px-4 py-2.5">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                Size
                            </p>
                            <p className="text-sm font-semibold">
                                {databaseInfo.databaseSize}
                            </p>
                        </div>
                        <div className="px-4 py-2.5">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                Connections
                            </p>
                            <p className="text-sm font-semibold">
                                {databaseInfo.connectionCount}
                            </p>
                        </div>
                        <div className="px-4 py-2.5">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                Migrations
                            </p>
                            <p className="text-sm font-semibold">
                                {migration?.totalCount ?? '—'}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                            <div className="flex items-center gap-1.5 mb-2">
                                <Layers className="h-3 w-3 text-muted-foreground" />
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                    Extensions
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {databaseInfo.extensions.map((ext) => (
                                    <Badge
                                        key={ext}
                                        variant="secondary"
                                        className="text-xs font-mono px-2 py-0.5"
                                    >
                                        {ext}
                                        <CheckCircle2 className="h-3 w-3 text-primary ml-1" />
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="px-4 py-3">
                            <div className="flex items-center gap-1.5 mb-2">
                                <GitBranch className="h-3 w-3 text-muted-foreground" />
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                    Latest Migration
                                </span>
                                {migration && (
                                    <CheckCircle2 className="h-3 w-3 text-primary" />
                                )}
                            </div>
                            {migration ? (
                                <>
                                    <p
                                        className="text-xs font-mono text-foreground truncate"
                                        title={migration.latestName}
                                    >
                                        {migration.latestName}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                        {migration.latestDate}
                                    </p>
                                </>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    No migrations
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            )}
        </Card>
    );
}
